import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, model, output } from '@angular/core';
import { IonButton, IonContent, IonHeader, IonIcon, IonSegment, IonSegmentButton, IonToolbar } from '@ionic/angular';
import { TranslatePipe } from '@ngx-translate/core';
import { User } from '../../entities/user';
import { UserRoles } from '../../entities/user-roles';
import { UserMainInfoComponent } from '../user-main-info/user-main-info.component';
import { UserMainInfoConfig } from '../user-main-info/entities/user-main-info-config';
import { CurafidaSegmentItem } from '../../../common/entities/curafida-segment.item';
import { SegmentType } from '../../../common/entities/segment.type';
import { IconColour, IconSizeTyp } from '../../../common/entities/icon_type';
import { CurafidaAppIconComponent } from '../../../common/components/curafida-app-icon/curafida-app-icon.component';
import { NoteListComponent, noteListFilterSettingName } from '../../../note/components/note-list/note-list.component';
import {
    TASK_LIST_FILTER_SETTING_NAME,
    TaskListComponent,
} from '../../../therapy/components/task/task-list/task-list.component';

/**
 * The content of the patient's side panel (lib-patient-side-panel): the patient's master data, notes and tasks, one
 * tab each. It opens on the tab its host hands over as `activeTab`, the master data by default. The side panel renders
 * it only while open, so its tabs load fresh every time it opens; within one opening, a tab stays rendered once opened,
 * so switching back to it shows it at once instead of loading it again.
 */
@Component({
    selector: 'lib-patient-side-panel-content',
    templateUrl: './patient-side-panel-content.component.html',
    styleUrls: ['./patient-side-panel-content.component.scss'],
    // Lays out its header and scrolling content the way Ionic lays out a page, inside the menu that holds it
    host: { class: 'ion-page' },
    // user-main-info is still mutation-based; an OnPush host would skip refreshing it after its async loads.
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        IonHeader,
        IonToolbar,
        IonSegment,
        IonSegmentButton,
        IonButton,
        IonIcon,
        IonContent,
        TranslatePipe,
        CurafidaAppIconComponent,
        UserMainInfoComponent,
        NoteListComponent,
        TaskListComponent,
    ],
})
export class PatientSidePanelContentComponent {
    readonly patient = input.required<User>();
    /** The reloaded patient after the master data was saved in the panel */
    readonly patientChange = output<User>();
    /** Asks the host to close the panel */
    readonly closed = output<void>();
    /** Tasks were changed in the panel, so the host can reload what it shows of them */
    readonly tasksChange = output<void>();
    /** Notes were changed in the panel, so the host can reload what it shows of them */
    readonly notesChange = output<void>();
    /** The tabs the logged-in user may open; the patient record decides them, see UserDetailTabs.sidePanelItems(). */
    readonly tabs = input<CurafidaSegmentItem<SegmentType>[]>([]);

    /** The latest state of the patient, so a tab opened again after saving does not show the data from before. */
    protected readonly currentPatient = linkedSignal(() => this.patient());
    /** The tab chosen; bound two-way, so the host can open the panel on the tab it was left on */
    readonly activeTab = model<SegmentType>(SegmentType.MAIN_INFORMATION);
    /** The tab chosen while the user may still open it, else the master data */
    protected readonly shownTab = computed(() =>
        this.tabs().some((item) => item.value === this.activeTab()) ? this.activeTab() : SegmentType.MAIN_INFORMATION,
    );

    /** The tabs opened since the panel opened; they stay rendered, so switching back shows them at once */
    protected readonly openedTabs = linkedSignal<SegmentType, ReadonlySet<SegmentType>>({
        source: this.shownTab,
        computation: (tab, previous) => new Set([...(previous?.value ?? []), tab]),
    });

    protected readonly SegmentType = SegmentType;
    protected readonly UserRoles = UserRoles;
    protected readonly IconColour = IconColour;
    protected readonly IconSizeTyp = IconSizeTyp;
    // The panel sits on top of the patient record already, so saving must not navigate to it again.
    protected readonly config = new UserMainInfoConfig({ navigateOnEdit: false });
    // Its own tab, search, sort and paging, so paging the panel does not page the task list of the record ...
    protected readonly taskListFilterSettingName = 'TaskListComponent_MasterDataPanel';
    // ... but the same filter criteria, so filtering either list filters both.
    protected readonly TASK_LIST_FILTER_SETTING_NAME = TASK_LIST_FILTER_SETTING_NAME;
    // Likewise its own search, sort and paging for the note list, but the same tags as the note list of the record
    protected readonly noteListFilterSettingName = 'NoteListComponent_MasterDataPanel';
    protected readonly NOTE_LIST_FILTER_SETTING_NAME = noteListFilterSettingName;

    selectTab(tab: SegmentType): void {
        if (this.tabs().some((item) => item.value === tab)) this.activeTab.set(tab);
    }

    onPatientChange(patient: User): void {
        this.currentPatient.set(patient);
        this.patientChange.emit(patient);
    }

    close(): void {
        this.closed.emit();
    }
}
